/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MenuIcon, XIcon } from "@heroicons/react/outline";
import { classNames } from "../../helpers";
import { Link, Outlet, useLocation } from "react-router-dom";
import UserProfilePictureAvatar from "../avatar/UserProfilePictureAvatar";
import NestedMenuItem from "./NestedMenuItem";

type SidebarProps = {
  navigation?:
    | {
        name: string;
        href: string;
        icon: JSX.Element | any;
        className?: string;
        separate?: "top" | "bottom" | any;
        counter?: string | number;
        children?: { name: string; href: string }[];
      }[]
    | null;
  img?: string;
  sideHeader?: any;
  sideFooter?: any;
};
export default function Sidebar({
  navigation,
  sideHeader,
  img,
  sideFooter,
}: SidebarProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <div className="h-screen flex">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="ltr:-translate-x-full rtl:translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="ltr:-translate-x-full rtl:translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white focus:outline-none ">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 ltr:right-0 rtl:left-0 ltr:-mr-12 rtl:-ml-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-1 h-0 border-t border-gray-200 pb-4 overflow-y-auto">
                {sideHeader}
                <nav aria-label="Sidebar" className="mt-5">
                  <div className="px-2 space-y-1">
                    {!!navigation &&
                      navigation.map((item) => (
                        <div key={item.name + "_lg_screens"}>
                          {item.separate === "top" && (
                            <div className="border-t border-gray-200 h-[1px]"></div>
                          )}
                          {!!item?.children && item?.children.length > 0 ? (
                            <NestedMenuItem item={item} />
                          ) : (
                            <Link
                              to={item.href}
                              onClick={() => setSidebarOpen(false)}
                              className={classNames(
                                pathname === item.href
                                  ? //   item.current
                                    "bg-gray-200 text-gray-900"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                                !!item.className && item.className,
                                "group flex items-center px-2 my-2 py-2 text-sm font-medium rounded-md"
                              )}
                            >
                              <item.icon
                                className={classNames(
                                  // item.current
                                  pathname === item.href
                                    ? "text-gray-500"
                                    : "text-gray-400 group-hover:text-gray-500",
                                  "ltr:mr-3 rtl:ml-3 h-6 w-6"
                                )}
                                aria-hidden="true"
                              />
                              {item.name}
                              {!!item.counter && (
                                <div className="bg-gray-200 w-8 h-5 text-center rounded-full ltr:mr-auto rtl:mr-auto">
                                  {item.counter}
                                </div>
                              )}
                            </Link>
                          )}
                          {item.separate === "bottom" && (
                            <div className="border-t border-gray-200 h-[1px]"></div>
                          )}
                        </div>
                      ))}
                  </div>
                </nav>
              </div>
              <div className="flex-shrink-0  p-4">{sideFooter}</div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true">
            {/* Force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex-1 flex flex-col min-h-0 ltr:border-r rtl:border-l border-gray-200 bg-gray-100">
            <div className="flex-1 flex flex-col pt-8 pb-4 overflow-y-auto">
              <div className="text-center px-4">{sideHeader}</div>

              <nav className=" flex-1" aria-label="Sidebar">
                <div className="px-2 ">
                  {!!navigation &&
                    navigation.map((item) => (
                      <div key={item.name + "_lg_screens"}>
                        {item.separate === "top" && (
                          <div className="border-t border-gray-200 h-[1px]"></div>
                        )}
                        {!!item?.children && item?.children.length > 0 ? (
                          <NestedMenuItem item={item} />
                        ) : (
                          <Link
                            to={item.href}
                            className={classNames(
                              pathname === item.href
                                ? //   item.current
                                  "bg-gray-200 text-gray-900"
                                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                              !!item.className && item.className,
                              "group flex items-center px-2 my-2 py-2 text-sm font-medium rounded-md"
                            )}
                          >
                            <item.icon
                              className={classNames(
                                // item.current
                                pathname === item.href
                                  ? "text-gray-500"
                                  : "text-gray-400 group-hover:text-gray-500",
                                "ltr:mr-3 rtl:ml-3 h-6 w-6"
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                            {!!item.counter && (
                              <div className="bg-gray-200 w-8 h-5 text-center rounded-full ltr:ml-auto rtl:mr-auto">
                                {item.counter}
                              </div>
                            )}
                          </Link>
                        )}
                        {item.separate === "bottom" && (
                          <div className="border-t border-gray-200 h-[1px]"></div>
                        )}
                      </div>
                    ))}
                </div>
              </nav>
            </div>
            <div className="flex-shrink-0 ">{sideFooter}</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-1.5">
            <div>
              <UserProfilePictureAvatar
                profilePictureUrl={img}
                className="w-8 h-8"
                alt="Workflow"
              />
            </div>
            <div>
              <button
                type="button"
                data-cy="sidebarOpen"
                className="-mr-3 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <MenuIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 h-full relative z-0 flex overflow-y-auto overflow-x-hidden">
          {!!navigation && <Outlet />}
        </div>
      </div>
    </div>
  );
}
